// One account in the Review tab: its contacts, their three emails, and the three decisions.
//
// Edit-then-approve edits in place: the textareas replace the copy, and Approve sends the edited
// steps for every contact that changed. The server re-checks every limit and runs every rail
// again; this card only stops an edit it already knows will be refused.

import { useState } from 'react';
import type { ContactContest, ContactEditBody, SendContact, SendRow, SendStep } from '../api/types';
import {
  REJECT_REASON_WORDS, contactOutcomeWord, contestScoresLine, editProblem, finalSourceLine, needsResolve,
  ownerTone, positionScoreLines, priorityLabel, sendableLine, showContactDetail, statusTone, statusWord, stepsChanged,
  tierReasonLine,
} from '../lib/sends';
import { LinkedText } from '../components/LinkedText';
import { LosingSequences } from './LosingSequences';
import { StepView } from './StepView';

function StepEditor({
  steps,
  onChange,
}: {
  steps: SendStep[];
  onChange: (next: SendStep[]) => void;
}) {
  function setSubject(value: string) {
    onChange(steps.map((step, index) => (index === 0 ? { ...step, subject: value } : step)));
  }
  function setBody(position: number, value: string) {
    onChange(steps.map((step, index) => (index === position ? { ...step, body: value } : step)));
  }
  return (
    <div className="sendeditor">
      <label className="sendlabel">
        Subject
        <input className="sendinput" value={steps[0]?.subject ?? ''} onChange={event => setSubject(event.target.value)} />
      </label>
      {steps.map((step, index) => (
        <label className="sendlabel" key={index}>
          Email {index + 1} <span className="sendquiet">{step.body.length}/1024</span>
          <textarea className="sendtextarea" rows={7} value={step.body}
            onChange={event => setBody(index, event.target.value)} />
        </label>
      ))}
    </div>
  );
}

// The writers' contest: the winner, every score and the buyer's why; the losing sequences behind a
// toggle that fetches them (`LosingSequences`), since the queue read leaves them out.
function ContestSummary({ accountId, contactKey, contest }: { accountId: string; contactKey: string; contest: ContactContest }) {
  const sendable = sendableLine(contest);
  const finalSource = finalSourceLine(contest);
  const positionLines = positionScoreLines(contest);
  return (
    <div className="sendcontest">
      {sendable ? <p className={'sendnote ' + (contest.sendable ? 'ok' : 'warn')}>{sendable}</p> : null}
      <p className="sendquiet sendwhy">
        Winner: <b>{contest.buyer.winner}</b> · {contestScoresLine(contest)}
      </p>
      {finalSource ? <p className="sendquiet sendwhy">{finalSource}</p> : null}
      {positionLines.length ? (
        <details className="sendlosers">
          <summary className="sendquiet">Scores per email</summary>
          {positionLines.map(line => <p key={line} className="sendquiet sendwhy">{line}</p>)}
        </details>
      ) : null}
      {contest.buyer.why ? <p className="sendquiet sendwhy">Buyer: {contest.buyer.why}</p> : null}
      <LosingSequences accountId={accountId} contactKey={contactKey} contest={contest} />
    </div>
  );
}

function ContactBlock({
  accountId,
  contact,
  editing,
  draftSteps,
  onDraft,
}: {
  accountId: string;
  contact: SendContact;
  editing: boolean;
  draftSteps: SendStep[];
  onDraft: (next: SendStep[]) => void;
}) {
  return (
    <div className="sendcontact">
      <div className="sendcontacthead">
        <b>{contact.name}</b>
        {contact.title ? <span> · {contact.title}</span> : null}
        <span className="sendquiet"> · {contact.email}</span>
        {contact.edited ? <span className="chip done">edited</span> : null}
        {contact.push_outcome ? <span className="chip">{contactOutcomeWord(contact.push_outcome)}</span> : null}
      </div>
      {contact.hero_because ? <p className="sendquiet sendwhy">{contact.hero_because}</p> : null}
      {contact.push_detail && showContactDetail(contact.push_outcome) ? <p className="sendquiet sendwhy">{contact.push_detail}</p> : null}
      {contact.rule_note ? <p className="sendquiet sendwhy">{contact.rule_note}</p> : null}
      {contact.contest ? <ContestSummary accountId={accountId} contactKey={contact.key} contest={contact.contest} /> : null}
      {editing
        ? <StepEditor steps={draftSteps} onChange={onDraft} />
        : contact.steps.map((step, index) => <StepView key={index} step={step} index={index} />)}
    </div>
  );
}

export function SendRowCard({
  row,
  rejectReasons,
  selectable,
  selected,
  busy,
  onToggle,
  onApprove,
  onReject,
  onResolve,
}: {
  row: SendRow;
  rejectReasons: string[];
  selectable: boolean;
  selected: boolean;
  busy: boolean;
  onToggle: (accountId: string) => void;
  onApprove: (accountId: string, edits: ContactEditBody[]) => void;
  onReject: (accountId: string, reason: string, note: string) => void;
  onResolve: (accountId: string, outcome: 'sent' | 'not_sent') => void;
}) {
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, SendStep[]>>({});
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState(rejectReasons[0] ?? 'other');
  const [note, setNote] = useState('');
  const resolvable = needsResolve(row.status);
  const decidable = row.status !== 'sent' && row.status !== 'rejected' && row.status !== 'stale' && !resolvable;

  function stepsFor(contact: SendContact): SendStep[] {
    return drafts[contact.key] ?? contact.steps;
  }

  // Only contacts whose copy changed are sent as edits; a refused edit never leaves the card.
  const edits: ContactEditBody[] = row.contacts
    .filter(contact => editing && stepsChanged(contact.steps, stepsFor(contact)))
    .map(contact => ({ contact_key: contact.key, steps: stepsFor(contact) }));
  const editError = edits.map(edit => editProblem(edit.steps)).find(problem => problem !== null) ?? null;

  return (
    <article className={'sendcard tone-' + statusTone(row.status)}>
      <header className="sendcardhead">
        {selectable ? (
          <input type="checkbox" checked={selected} onChange={() => onToggle(row.account_id)}
            aria-label={`Select ${row.account}`} />
        ) : null}
        <div className="sendtitle">
          <b>{row.account}</b> <span className="sendquiet">{row.domain} · {priorityLabel(row.priority)}
            {row.sender_email ? ` · from ${row.sender_email}` : ''}</span>
        </div>
        <span className={'chip sendstatus ' + statusTone(row.status)}>{statusWord(row.status)}</span>
      </header>
      {row.signal_fact ? <p className="sendsignal"><LinkedText text={row.signal_fact} /></p> : null}
      {row.tier_reason ? <p className="sendquiet sendwhy">{tierReasonLine(row.tier_reason)}</p> : null}
      {row.retirement_note ? <p className="sendquiet sendretired">{row.retirement_note}</p> : null}
      {/* The facts the copy was written from, so a claim in an email can be checked here. */}
      {row.facts_used.length ? (
        <ul className="sendfacts" aria-label="Facts the emails use">
          {row.facts_used.map(fact => <li key={fact}>{fact}</li>)}
        </ul>
      ) : null}
      {row.hold_reason ? <p className={'sendnote ' + (row.status === 'dry_run' ? '' : 'warn')}>{row.hold_reason}</p> : null}
      {row.sequence_note ? <p className={'sendnote ' + (row.sequence_ready ? 'ok' : 'warn')}>{row.sequence_note}</p> : null}
      {row.error ? <p className="sendnote bad">{row.error}</p> : null}
      {row.contest_note ? <p className="sendnote warn">{row.contest_note}</p> : null}
      {row.owner_note ? <p className={'sendnote ' + ownerTone(row.owner_outcome)}>{row.owner_note}</p> : null}
      {row.reject_reason ? (
        <p className="sendnote">Rejected: {REJECT_REASON_WORDS[row.reject_reason] ?? row.reject_reason}
          {row.reject_note ? ` — ${row.reject_note}` : ''}</p>
      ) : null}

      {row.contacts.map(contact => (
        <ContactBlock key={contact.key} accountId={row.account_id} contact={contact} editing={editing} draftSteps={stepsFor(contact)}
          onDraft={next => setDrafts(current => ({ ...current, [contact.key]: next }))} />
      ))}

      {resolvable ? (
        <footer className="sendactions">
          <span className="sendnote">The push may have landed. Look for these leads in the sequence, then say which.</span>
          <button className="btn" type="button" disabled={busy} onClick={() => onResolve(row.account_id, 'sent')}>
            They are in Amplemarket
          </button>
          <button className="btn" type="button" disabled={busy} onClick={() => onResolve(row.account_id, 'not_sent')}>
            Not in Amplemarket
          </button>
        </footer>
      ) : null}
      {decidable ? (
        <footer className="sendactions">
          <button className="btn primary" type="button" disabled={busy || Boolean(editError)}
            onClick={() => onApprove(row.account_id, edits)}>
            {edits.length ? 'Approve edited' : 'Approve'}
          </button>
          <button className="btn" type="button" disabled={busy} onClick={() => setEditing(!editing)}>
            {editing ? 'Stop editing' : 'Edit'}
          </button>
          <button className="btn" type="button" disabled={busy} onClick={() => setRejecting(!rejecting)}>Reject</button>
          {editError ? <span className="sendnote bad">{editError}</span> : null}
          {rejecting ? (
            <span className="sendreject">
              <select value={reason} onChange={event => setReason(event.target.value)}>
                {rejectReasons.map(option => (
                  <option key={option} value={option}>{REJECT_REASON_WORDS[option] ?? option}</option>
                ))}
              </select>
              <input className="sendinput" placeholder="Note (optional)" value={note}
                onChange={event => setNote(event.target.value)} />
              <button className="btn" type="button" disabled={busy}
                onClick={() => onReject(row.account_id, reason, note)}>Reject with reason</button>
            </span>
          ) : null}
        </footer>
      ) : null}
    </article>
  );
}
